'use client';

import React, { Suspense, useRef } from 'react';
import { useIntersectionObserver } from '@benzinga/hooks';
import type { NetWorthActivityChart } from './NetWorthActivityChart';
import type { MonthlyTradingPattern } from './MonthlyTradingPattern';
import { ChartSkeleton } from './ChartSkeleton';

type NetWorthActivityChartProps = React.ComponentProps<typeof NetWorthActivityChart>;
type MonthlyTradingPatternProps = React.ComponentProps<typeof MonthlyTradingPattern>;

const NET_WORTH_CARD_HEIGHT = 392;
const MONTHLY_CARD_HEIGHT = 312;

const NetWorthActivityChartLazy = React.lazy(() =>
  import('./NetWorthActivityChart').then(m => ({ default: m.NetWorthActivityChart })),
);
const MonthlyTradingPatternLazy = React.lazy(() =>
  import('./MonthlyTradingPattern').then(m => ({ default: m.MonthlyTradingPattern })),
);

interface DeferredChartProps {
  cardHeight: number;
  className?: string;
  children: React.ReactNode;
  skeletonVariant: 'activity' | 'monthly';
}

function DeferredChart({ cardHeight, className, children, skeletonVariant }: DeferredChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(ref as React.RefObject<Element>, {
    freezeOnceVisible: true,
    rootMargin: '200px',
  });
  const isVisible = !!entry?.isIntersecting;

  return (
    <div ref={ref} className={className} style={{ minHeight: cardHeight }}>
      {isVisible ? (
        <Suspense fallback={<ChartSkeleton height={cardHeight} variant={skeletonVariant} />}>{children}</Suspense>
      ) : (
        <ChartSkeleton height={cardHeight} variant={skeletonVariant} />
      )}
    </div>
  );
}

export function LazyNetWorthActivityChart(props: NetWorthActivityChartProps) {
  return (
    <DeferredChart cardHeight={NET_WORTH_CARD_HEIGHT} skeletonVariant="activity">
      <NetWorthActivityChartLazy {...props} />
    </DeferredChart>
  );
}

export function LazyMonthlyTradingPattern(props: MonthlyTradingPatternProps) {
  return (
    <DeferredChart cardHeight={MONTHLY_CARD_HEIGHT} className="h-full" skeletonVariant="monthly">
      <MonthlyTradingPatternLazy {...props} />
    </DeferredChart>
  );
}
